import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import apiClient from '@/api/client';
import router from '@/router';
import type { UserRole } from '@hyperke/shared';

/**
 * User interface
 */
export interface User {
  id: string;
  email: string;
  full_name: string;
  role: UserRole;
  is_active: boolean;
  must_change_password?: boolean;
  admin_user_id?: string; // Present during impersonation
  is_impersonation?: boolean; // Flag to indicate impersonation session
}

/**
 * Login request
 */
export interface LoginRequest {
  email: string;
  password: string;
}

/**
 * Login response
 */
export interface LoginResponse {
  token: string;
  user: User;
}

/**
 * Auth store using Pinia
 * Manages authentication state and actions
 */
export const useAuthStore = defineStore('auth', () => {
  console.log('[Auth Store] Store INITIALIZING - reading localStorage directly');

  // Initialize from localStorage
  const storedToken = localStorage.getItem('token');
  const storedUser = localStorage.getItem('user');

  console.log('[Auth Store] Initial localStorage read:', {
    hasToken: !!storedToken,
    tokenPrefix: storedToken ? storedToken.substring(0, 20) + '...' : 'none',
    hasUser: !!storedUser,
  });

  const user = ref<User | null>(storedUser ? JSON.parse(storedUser) : null);
  const token = ref<string | null>(storedToken);
  const loading = ref(false);

  // Impersonation state
  const originalAdminToken = ref<string | null>(localStorage.getItem('original_admin_token'));
  const isImpersonating = computed(() => user.value?.is_impersonation === true);

  const isAuthenticated = computed(() => !!token.value && !!user.value);

  console.log('[Auth Store] Store initialized:', {
    tokenSet: !!token.value,
    userSet: !!user.value,
    userRole: user.value?.role,
    isAuthenticated: isAuthenticated.value,
  });

  /**
   * Login user
   * 
   * @param email - User email
   * @param password - User password
   * @returns Promise<void>
   */
  async function login(email: string, password: string): Promise<void> {
    loading.value = true;
    console.log('[Auth Store] Login attempt:', { email });
    try {
      const response = await apiClient.post<LoginResponse>('/auth/login', {
        email,
        password,
      });

      console.log('[Auth Store] Login successful:', {
        hasToken: !!response.data.token,
        tokenLength: response.data.token?.length || 0,
        tokenPrefix: response.data.token?.substring(0, 20) + '...',
        user: response.data.user,
        mustChangePassword: response.data.user.must_change_password,
      });

      // Persist token to localStorage FIRST (before updating store)
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      console.log('[Auth Store] Token saved to localStorage');

      // Then update store (this ensures localStorage is the source of truth)
      token.value = response.data.token;
      user.value = response.data.user;
      console.log('[Auth Store] Store updated:', {
        tokenSet: !!token.value,
        userSet: !!user.value,
        isAuthenticated: isAuthenticated.value,
        mustChangePassword: user.value?.must_change_password,
      });

      // Check if user must change password (first-time login)
      // Check both the response and the store value
      const mustChange = response.data.user.must_change_password === true || 
                        user.value?.must_change_password === true;
      console.log('[Auth Store] Checking password change requirement:', {
        mustChangePassword: mustChange,
        responseValue: response.data.user.must_change_password,
        storeValue: user.value?.must_change_password,
      });
      
      // Use nextTick to ensure router is ready
      await new Promise(resolve => setTimeout(resolve, 50));
      
      if (mustChange) {
        console.log('[Auth Store] User must change password, redirecting to ChangePassword');
        router.push({ name: 'ChangePassword', replace: true }).catch((err) => {
          console.error('[Auth Store] Router push error:', err);
          // Fallback: force navigation
          window.location.href = '/change-password';
        });
      } else {
        console.log('[Auth Store] No password change required, redirecting to Leads');
        // Redirect to leads page - use replace to avoid back button issues
        router.push({ name: 'Leads', replace: true }).catch((err) => {
          console.error('[Auth Store] Router push error:', err);
          // Fallback: force navigation
          window.location.href = '/leads';
        });
      }
    } catch (error) {
      console.error('[Auth Store] Login error:', error);
      throw error;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Logout user
   * Clears all storage (localStorage, sessionStorage, cookies), invalidates session, redirects to login
   * P0.2 Fix: Comprehensive logout to prevent session leaks
   */
  function logout(): void {
    // PERSISTENT LOG: Write to sessionStorage BEFORE clearing it
    // This survives the page clear so we can debug
    const logoutInfo = {
      timestamp: new Date().toISOString(),
      stackTrace: new Error().stack?.split('\n').slice(1, 8).map(line => line.trim()),
      storeStateBefore: {
        hasToken: !!token.value,
        hasUser: !!user.value,
        userRole: user.value?.role,
        userEmail: user.value?.email,
      },
      localStorageStateBefore: {
        hasToken: !!localStorage.getItem('token'),
        hasUser: !!localStorage.getItem('user'),
        tokenPrefix: localStorage.getItem('token')?.substring(0, 20) + '...',
        userPreview: localStorage.getItem('user')?.substring(0, 100) + '...',
      },
      url: window.location.href,
    };

    // Store in sessionStorage with a unique key (we'll clear sessionStorage later, but read this first)
    sessionStorage.setItem('__hyperke_logout_debug__', JSON.stringify(logoutInfo));

    console.log('[Auth Store] Logout TRIGGERED - persistent log written to sessionStorage: __hyperke_logout_debug__');

    // Clear store state
    token.value = null;
    user.value = null;

    // Clear localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    // Clear sessionStorage (comprehensive cleanup)
    // But keep our debug log for one more read
    const debugLog = sessionStorage.getItem('__hyperke_logout_debug__');
    sessionStorage.clear();
    if (debugLog) {
      sessionStorage.setItem('__hyperke_logout_debug__', debugLog);
    }
    
    // Clear cookies (if any exist)
    document.cookie.split(';').forEach(cookie => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      // Clear cookie by setting it to expire in the past
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
    });
    
    // Invalidate token on server (if JWT blacklist exists)
    // Note: This is optional - most JWT implementations are stateless
    // If you have a logout endpoint, call it here:
    // apiClient.post('/auth/logout').catch(() => {}); // Ignore errors
    
    console.log('[Auth Store] All storage cleared, redirecting to login');
    
    // Force hard redirect to ensure no cached state
    // Use window.location for absolute guarantee of navigation
    window.location.href = '/login';
    
    // Fallback: router push (in case window.location doesn't work)
    router.push({ name: 'Login', replace: true }).catch(() => {
      // If router fails, force navigation
      window.location.href = '/login';
    });
  }

  /**
   * Check auth status
   * Loads user from localStorage if token exists
   */
  function checkAuth(): void {
    console.log('[Auth Store] checkAuth() called - reading from localStorage');

    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    console.log('[Auth Store] checkAuth() localStorage state:', {
      hasToken: !!storedToken,
      tokenPrefix: storedToken ? storedToken.substring(0, 20) + '...' : 'none',
      hasUser: !!storedUser,
      userPreview: storedUser ? storedUser.substring(0, 100) + '...' : 'none',
    });

    if (storedToken && storedUser) {
      token.value = storedToken;
      try {
        const parsedUser = JSON.parse(storedUser);
        console.log('[Auth Store] checkAuth() parsed user:', {
          email: parsedUser?.email,
          role: parsedUser?.role,
          must_change_password: parsedUser?.must_change_password,
          parseSuccess: true,
        });

        // Ensure must_change_password field exists (for backward compatibility)
        if (parsedUser && typeof parsedUser.must_change_password === 'undefined') {
          parsedUser.must_change_password = false;
          console.log('[Auth Store] checkAuth() added missing must_change_password field');
        }
        user.value = parsedUser;

        console.log('[Auth Store] checkAuth() store updated:', {
          tokenSet: !!token.value,
          userSet: !!user.value,
          isAuthenticated: isAuthenticated.value,
        });
      } catch (error) {
        console.error('[Auth Store] checkAuth() JSON parse error, logging out:', error);
        // Invalid stored user, clear auth
        logout();
      }
    } else {
      console.log('[Auth Store] checkAuth() no token/user in localStorage, store remains empty');
    }
  }

  /**
   * Start impersonating a user
   * Stores the original admin token and switches to impersonation token
   *
   * @param impersonationToken - Impersonation JWT token
   * @param impersonatedUser - User data for impersonated user
   */
  async function startImpersonation(
    impersonationToken: string,
    impersonatedUser: User
  ): Promise<void> {
    console.log('[Auth Store] Starting impersonation:', {
      impersonatedUser: impersonatedUser.email,
      hasOriginalToken: !!token.value,
    });

    // Store original admin token
    if (token.value) {
      originalAdminToken.value = token.value;
      localStorage.setItem('original_admin_token', token.value);
    }

    // Update with impersonation data
    localStorage.setItem('token', impersonationToken);
    localStorage.setItem('user', JSON.stringify(impersonatedUser));

    token.value = impersonationToken;
    user.value = impersonatedUser;

    console.log('[Auth Store] Impersonation started:', {
      isImpersonating: isImpersonating.value,
      adminUserId: impersonatedUser.admin_user_id,
    });
  }

  /**
   * End impersonation session
   * Restores the original admin token and user data
   *
   * @param impersonatedUserId - ID of the user being impersonated
   */
  async function endImpersonation(impersonatedUserId: string): Promise<void> {
    console.log('[Auth Store] Ending impersonation:', {
      impersonatedUserId,
      hasOriginalToken: !!originalAdminToken.value,
    });

    // Call API to end session (using admin token)
    if (originalAdminToken.value) {
      try {
        await apiClient.post('/admin/end-impersonation', {
          impersonated_user_id: impersonatedUserId,
          end_reason: 'logout',
        }, {
          headers: {
            Authorization: `Bearer ${originalAdminToken.value}`,
          },
        });
        console.log('[Auth Store] Impersonation session ended on server');
      } catch (error) {
        console.error('[Auth Store] Failed to end impersonation session:', error);
        // Continue anyway - we want to restore admin session regardless
      }
    }

    // Restore original admin token
    if (originalAdminToken.value) {
      // Restore token to localStorage
      localStorage.setItem('token', originalAdminToken.value);
      token.value = originalAdminToken.value;

      // Clear impersonation flags from user object
      // The next API call will refresh the user data with the correct admin info
      if (user.value) {
        user.value.is_impersonation = false;
        delete user.value.admin_user_id;
        localStorage.setItem('user', JSON.stringify(user.value));
      }

      console.log('[Auth Store] Admin token restored, flags cleared');
    }

    // Clear original token storage
    originalAdminToken.value = null;
    localStorage.removeItem('original_admin_token');

    console.log('[Auth Store] Impersonation ended, reloading page to refresh user data');

    // Reload page to refresh user data with admin token
    setTimeout(() => {
      window.location.reload();
    }, 100);
  }

  // Initialize auth on store creation
  checkAuth();

  return {
    user,
    token,
    loading,
    isAuthenticated,
    isImpersonating,
    originalAdminToken,
    login,
    logout,
    checkAuth,
    startImpersonation,
    endImpersonation,
  };
});